import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Download } from "lucide-react";
import OptionsChain from "./OptionsChain";
import OrderPanel from "./OrderPanel";
import ClientsTable from "./ClientsTable";
import Positions from "./Positions";
import Orders from "./Orders";
import AutoIndexTrade from "./AutoIndexTrade";
import AILearningLibrary from "./AILearningLibrary";
import TrendHealthPage from "./TrendHealthPage";
import IndexEMA21 from "./IndexEMA21";
import ExitAssistant from "./ExitAssistant";
import HybridAI from "./HybridAI";
import TradingPositions from "./TradingPositions";
import PerformanceDashboard from "./PerformanceDashboard";
import WorkspaceNavigation, { hasLoggedInTradingClient } from "./WorkspaceNavigation";
import AutoStockTrade from "./AutoStockTrade";
import EquitySwing from "./EquitySwing";
import AutoIndexBacktest from "./AutoIndexBacktest";
import TradeNotificationCenter from "./TradeNotificationCenter";
import StockOptions from "./StockOptions";
import "./dashboard.css";

export default function AdminDashboard({ onLogout, onSessionExpired }) {

  const API_BASE = "/api";

  const getWsUrl = () => {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}${API_BASE}/ws`;
  };
  const wsRef = useRef(null);
  const systemReadyRef = useRef(false);
  const lastWsMessageAtRef = useRef(Date.now());
  const wsReconnectTimerRef = useRef(null);
  const wsIntentionalCloseRef = useRef(false);
  const wsAuthCheckRef = useRef(false);
  const [wsStatus, setWsStatus] = useState("CONNECTING");
  const [loadingClients, setLoadingClients] = useState({});
  const loginPollRef = useRef({});
  const adminLoginPollRef = useRef(null);
  const [activeTab, setActiveTab] = useState("CLIENTS");
  useEffect(() => { document.querySelector(".workspace-content")?.scrollTo?.({ top: 0, left: 0 }); }, [activeTab]);
  const [equityTab, setEquityTab] = useState("STOCK_OPTIONS");
  const adminRedirectedRef = useRef(false);
  const [algoData, setAlgoData] = useState({
    running: false,
    pnl: 0,
    trades: 0,
    ce_active: false,
    pe_active: false
  });
  const activeProductionAlgo = "NIFTY";
  const [indexView, setIndexView] = useState("EMA");
  const [aiView, setAiView] = useState("HYBRID_AI");
  const [activeAlgoFamily, setActiveAlgoFamily] = useState("NIFTY_EMA_FLIP");
  const activeFamilyInstances = (
    algoData.families?.[activeAlgoFamily]?.instances
    || {}
  );
  const productionAlgoSymbols = ["NIFTY", "SENSEX"].filter(symbol => activeFamilyInstances[symbol]);
  const selectedFamilyInstances = Object.fromEntries(
    productionAlgoSymbols.map(symbol => [symbol, activeFamilyInstances[symbol]]),
  );

  useEffect(() => {
    if (algoData.active_family && algoData.active_family !== activeAlgoFamily) {
      setActiveAlgoFamily(algoData.active_family);
    }
  }, [algoData.active_family, activeAlgoFamily]);

  // 🔥 Combined state (prevents multiple re-renders) 
  const [dashboard, setDashboard] = useState({ 
    clients: [], 
    positions: [], 
    orders: [], 
    options: [], 
    atm: null, 
    expiry: null,
    spot: null,
    index: "NIFTY",
    trade_symbols: [],
    active_trade_symbol: null,
    chain_generation: 0,
    chain_loading: false,
    trade_lock: { locked: false, allowed: true, symbols: [] },
    copy_trade_trail: { active: false, status: "IDLE" },
    auto_stock: { running: false, stocks: [] }
  });

  const startAlgoRequest = async (instrument = null) => {
    try {
      const query = instrument ? `?instrument=${encodeURIComponent(instrument)}` : "";
      const res = await fetch(`${API_BASE}/algo/start${query}`, {
        method: "POST" 
      });
      const data = await res.json();

      if(!res.ok) {
        alert(`Failed to start algo: ${data.detail || data.error || "Unknown error"}`);
        return;
      }

    } catch (error) {
      alert("Failed to start algo");
    }
  };
 
  const [activeCompareSymbol, setActiveCompareSymbol] = useState("NIFTY");
  
  const compareSymbols = useMemo(
    () => algoData.compare_symbols || [],
    [algoData.compare_symbols]
  );

  useEffect(() => {
    if (
      compareSymbols.length > 0 &&
      !compareSymbols.includes(activeCompareSymbol)
    ) {
      setActiveCompareSymbol(compareSymbols[0]);
    }
  }, [compareSymbols, activeCompareSymbol]);

  const stopAlgoRequest = async (instrument = null) => {
    const query = instrument ? `?instrument=${encodeURIComponent(instrument)}` : "";
    const response = await fetch(`${API_BASE}/algo/stop${query}`, { method: "POST" });
    const result = await response.json();
    if (!response.ok || result.error) {
      throw new Error(result.detail || result.error || "Unable to stop ALGO");
    }
  };
  const stopAlgo = () => stopAlgoRequest(activeProductionAlgo);

  const startAlgo = () => startAlgoRequest(activeProductionAlgo);

  const startAlgoCompare = async () => {
    try {
      const res = await fetch(`${API_BASE}/algo/compare/start?max_trades=9999`, {
        method: "POST"
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.error || "Failed to start compare");
      }
    } catch (error) {
      alert("Failed to start algo compare");
    }
  };

  const stopAlgoCompare = async () => {
    try {
      await fetch(`${API_BASE}/algo/compare/stop`, {
        method: "POST"
      });
    } catch (error) {
      alert("Failed to stop algo compare");
    }
  };

  const getCompareRows = () => {
    if (!algoData.compare) return [];

    const symbolCompare = algoData.compare[activeCompareSymbol];

    if (!symbolCompare) return [];

    return Object.values(symbolCompare);
  };

  const fmtTime = (v) => {
    if (!v) return "-";

    try {
      const safeValue = typeof v === "string" ? v.replace(" ", "T") : v;
      const d = new Date(safeValue);

      if (isNaN(d.getTime())) return v;

      return d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      });
    } catch {
      return v;
    }
  };

  const downloadExcel = (rows, fileName, sheetName = "Sheet1") => {
    if (!rows || rows.length === 0) {
      alert("No data available to download");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName.slice(0, 31));

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream"
    });

    saveAs(blob, fileName);
  };

  const getSignalAuditRows = () => {
    return getCompareRows().flatMap((r) => {
      const skips = (r.last_10_skips || []).map((s) => ({
        algo: r.name,
        decision: "SKIPPED",
        ...s
      }));

      const last = r.last_signal
        ? [{
            algo: r.name,
            decision: r.last_signal.decision || "LAST_SIGNAL",
            ...r.last_signal
          }]
        : [];

      return [...last, ...skips].slice(0, 10);
    });
  };

  const getActiveTradeRows = () => {
    return getCompareRows().map((r) => ({
      "Compare Symbol": activeCompareSymbol,
      Algo: r.name,
      Status: r.active ? "Active" : r.running ? "Running" : "Waiting",
      Selection: r.selection || "-",
      Delta: r.delta ?? "-",
      IV: r.iv ?? "-",
      "Flip Time": fmtTime(r.flip_identified_time),
      "Flip Candle": fmtTime(r.flip_candle_time),
      "Entry Time": fmtTime(r.entry_time),
      Symbol: r.symbol || "-",
      Entry: r.entry ?? "-",
      SL: r.sl ?? "-",
      "Max Profit": r.max_profit ?? 0,
      "Max Loss": r.max_loss ?? 0
    }));
  };

  const getSummaryRows = () => {
    return getCompareRows().map((r) => ({
      "Compare Symbol": activeCompareSymbol,
      Strategy: r.name,
      Status: r.active ? "Active" : r.running ? "Running" : r.stopped ? "Stopped" : "Waiting",
      "Active Symbol": r.symbol || "-",
      Entry: r.entry ?? "-",
      SL: r.sl ?? "-",
      Trades: r.completed_trades ?? 0,
      Wins: r.wins ?? 0,
      Losses: r.losses ?? 0,
      "Win %": r.win_rate ?? 0,
      "Net Points": r.net_points ?? 0,
      "Profit Factor": r.profit_factor ?? 0,
      "Avg Win": r.avg_win ?? 0,
      "Avg Loss": r.avg_loss ?? 0,
      "Max DD": r.max_drawdown ?? 0,
      "Early Exits": r.early_exit_count ?? 0,
      "Good Early": r.good_early_exit_count ?? 0,
      "False Early": r.false_early_exit_count ?? 0,
      "Early Accuracy %": r.early_exit_accuracy ?? 0,
      "Avg Saved": r.avg_early_exit_points_saved ?? 0,
      "Avg Missed": r.avg_early_exit_profit_missed ?? 0,
      Selection: r.selection || "-",
      Delta: r.delta ?? "-",
      IV: r.iv ?? "-",
      Rank: r.rank ? `#${r.rank}` : "-"
    }));
  };

  const getCardSummaryRows = () => {
    return getCompareRows().map((r) => ({
      "Compare Symbol": activeCompareSymbol,
      Algo: r.name,
      Flips: r.flip_count ?? 0,
      Entered: r.entered_flip_count ?? 0,
      Skipped: r.skipped_flip_count ?? 0,
      Selection: r.selection || "-",
      Delta: r.delta ?? "-",
      IV: r.iv ?? "-",
      "Early Exits": r.early_exit_count ?? 0,
      "Good Early": r.good_early_exit_count ?? 0,
      "False Early": r.false_early_exit_count ?? 0,
      "Accuracy %": r.early_exit_accuracy ?? 0,
      "Avg Saved": r.avg_early_exit_points_saved ?? 0,
      "Avg Missed": r.avg_early_exit_profit_missed ?? 0,
    }));
  };

  const getFlipHistoryRows = () => {
    return getCompareRows().flatMap((r) => {
      const history = r.flip_history || [];

      const fallbackHistory = history.length
        ? history
        : [
            ...(r.last_signal ? [r.last_signal] : []),
            ...(r.last_10_skips || [])
          ];

      return fallbackHistory.map((f, idx) => ({
        "Compare Symbol": activeCompareSymbol,
        Algo: r.name,
        "#": idx + 1,
        Time: fmtTime(f.time || f.flip_identified_time),
        "Flip Time": fmtTime(f.flip_identified_time || f.time),
        "Candle Time": fmtTime(f.candle_time || f.flip_candle_time),
        Direction: f.direction || "-",
        Decision: f.decision || "FLIP_DETECTED",
        Reason: f.reason || "-",
        Confirmation: f.confirmation ?? "-",
        ADX: f.adx ?? "-",
        "ADX Pass": f.adx_pass ?? "-",
        ATR: f.atr ?? "-",
        "ATR MA": f.atr_ma ?? "-",
        "ATR Pass": f.atr_pass ?? "-",
        Delta: f.delta ?? "-",
        IV: f.iv ?? "-",
        Selection: f.selection || "-"
      }));
    });
  };

  const getTradeHistoryRows = () => {
    return getCompareRows().flatMap((r) => {
      const trades = r.trade_history || r.last_10_trades || [];

      return trades.map((t, idx) => ({
        "Compare Symbol": activeCompareSymbol,
        Algo: r.name,
        "#": idx + 1,
        "Entry Time": fmtTime(t.entry_time),
        "Exit Time": fmtTime(t.exit_time),
        "Entry Candle": fmtTime(t.entry_candle_time),
        "Flip Time": fmtTime(t.flip_identified_time),
        "Flip Candle": fmtTime(t.flip_candle_time),
        Direction: t.direction || t.last_direction || "-",
        Side: t.side || "-",
        Symbol: t.symbol || "-",
        Strike: t.strike ?? "-",
        Spot: t.spot ?? "-",
        ATM: t.atm ?? "-",
        Entry: t.entry ?? "-",
        Exit: t.exit ?? "-",
        PnL: t.pnl ?? 0,
        Reason: t.reason || "-",
        "Smart Score": t.smart_exit_score ?? "-",
        "Smart Reason": t.smart_exit_reason ?? "-",
        Selection: t.selection || "-",
        Delta: t.delta ?? "-",
        IV: t.iv ?? "-",
        "Max Profit": t.max_profit ?? 0,
        "Max Loss": t.max_loss ?? 0,
        "Exit Type": t.reason?.startsWith("SMART")
          ? "SMART"
          : "SUPER TREND",
      }));
    });
  };

  const todayFileDate = new Date().toISOString().slice(0, 10);

  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedTradeSymbol, setSelectedTradeSymbol] = useState("");
  const [chainSwitching, setChainSwitching] = useState(false);

  const [systemStatus, setSystemStatus] = useState({});
  const [loginStatus, setLoginStatus] = useState({});
  const [adminAccounts, setAdminAccounts] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState("");
  const [adminLoading, setAdminLoading] = useState(false);
  const [expiryType, setExpiryType] = useState("CURRENT");
  const systemReady = systemStatus.ready === true;

  const refreshSystemStatus = useCallback(async () => {
    const res = await fetch(`${API_BASE}/system-status`, { cache: "no-store" });
    const data = await res.json();
    setSystemStatus(data);
    return data;
  }, [API_BASE]);

  const loadAdminAccounts = useCallback(async () => {
    const res = await fetch(`${API_BASE}/admin/accounts`, { cache: "no-store" });
    const data = await res.json();
    const admins = data.admins || [];

    setAdminAccounts(admins);
    setSelectedAdminId(prev => prev || admins[0]?.id || "");

    return admins;
  }, [API_BASE]);

  useEffect(() => {
    systemReadyRef.current = systemReady;
  }, [systemReady]);

  useEffect(() => {
    const activeAdminId = loginStatus.active_admin_id;
    if (
      loginStatus.admin
      && activeAdminId
      && adminAccounts.some((admin) => admin.id === activeAdminId)
    ) {
      setSelectedAdminId(activeAdminId);
    }
  }, [
    loginStatus.admin,
    loginStatus.active_admin_id,
    adminAccounts,
  ]);
  // ---------------- LOGIN ----------------
  const loginAdmin = async () => {
    setAdminLoading(true);

    if (loginStatus.admin && !systemReady) {
      try {
        const res = await fetch(`${API_BASE}/admin/services/retry`, {
          method: "POST",
          cache: "no-store",
        });
        const data = await res.json();

        if (!res.ok) {
          alert(data.detail || "Admin service initialization failed");
        }

        await loadLoginStatus();
        await refreshSystemStatus();
      } catch (err) {
        console.error("Admin initialization retry error:", err);
        alert("Admin service initialization retry failed");
      } finally {
        setAdminLoading(false);
      }
      return;
    }

    const win = window.open("about:blank", "_blank");

    if (!win) {
      alert("Popup blocked! Please allow popups.");
      setAdminLoading(false);
      return;
    }

    try {
      const params = new URLSearchParams();

      if (selectedAdminId) {
        params.set("admin_id", selectedAdminId);
      }

      const query = params.toString();
      const res = await fetch(
        `${API_BASE}/admin/login-url${query ? `?${query}` : ""}`,
        { cache: "no-store" }
      );
      const data = await res.json();

      if (!res.ok || !data.login_url) {
        win.close();
        alert(data.detail || data.error || "Admin login URL failed");
        setAdminLoading(false);
        return;
      }

      if (data.admin_id) {
        setSelectedAdminId(data.admin_id);
      }
      win.location.href = data.login_url;
      startAdminLoginPoll();
    } catch (err) {
      console.error("Admin login error:", err);
      win.close();
      setAdminLoading(false);
    }
  };

  const loginClient = async (clientId) => {
    setLoadingClients(prev => ({ ...prev, [clientId]: true }));

    const win = window.open("about:blank", "_blank");

    if (!win) {
      alert("Popup blocked! Please allow popups.");
      setLoadingClients(prev => ({ ...prev, [clientId]: false }));
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/client/${clientId}/login-url`);
      const data = await res.json();
      
      if (data.login_url) {
        win.location.href = data.login_url;
        startClientLoginPoll(clientId);
        return;

      } 
      else if (data.type === "backend") {
        win.close();

        await refreshClientsAndLogin();

        if (!data.success) {
          alert(data.message || "AngelOne login failed");
        }
        setLoadingClients(prev => ({ ...prev, [clientId]: false }));
      } 
      else {
        win.close();
        setLoadingClients(prev => ({ ...prev, [clientId]: false }));
      }

    } catch (err) {
      console.error("Login error:", err);
      win.close();
      setLoadingClients(prev => ({ ...prev, [clientId]: false }));
    }
  };  

  // ---------------- STATUS ----------------
  // ---------------- SYSTEM STATUS POLLING ----------------
  useEffect(() => {
    let interval;

    const checkSystem = async () => {
      try {
        await refreshSystemStatus();

      } catch (err) {
        console.error("System status error:", err);
      }
    };

    checkSystem();
    interval = setInterval(checkSystem, systemReady ? 5000 : 1000);

    return () => clearInterval(interval);
  }, [systemReady, refreshSystemStatus]);


  const loadLoginStatus = useCallback(async () => {
    const res = await fetch(`${API_BASE}/login-status`, { cache: "no-store" });
    const data = await res.json();
    // ✅ Flatten structure
    const nextStatus = {
      ...(data.clients || {}),
      admin: data.admin || false,
      active_admin_id: data.active_admin_id,
      ready: data.ready === true,
      init_in_progress: data.init_in_progress === true,
      initialization_error: data.initialization_error || null
    };

    setLoginStatus(nextStatus);
    return nextStatus;
  }, [API_BASE]);

  const loadClients = useCallback(async () => {
    const res = await fetch(`${API_BASE}/load-clients`, { cache: "no-store" });
    const data = await res.json();

    setDashboard(prev => ({
      ...prev,
      clients: data.clients || []
    }));

    return data.clients || [];
  }, [API_BASE]);

  const refreshClientsAndLogin = useCallback(async () => {
    const [status] = await Promise.all([
      loadLoginStatus(),
      loadClients()
    ]);

    return status;
  }, [loadClients, loadLoginStatus]);

  const startAdminLoginPoll = () => {
    if (adminLoginPollRef.current) {
      clearInterval(adminLoginPollRef.current);
    }

    const startedAt = Date.now();

    adminLoginPollRef.current = setInterval(async () => {
      try {
        const [login, status] = await Promise.all([
          loadLoginStatus(),
          refreshSystemStatus()
        ]);
        const timedOut = Date.now() - startedAt > 240000;

        if ((login.admin && !status.init_in_progress) || timedOut) {
          clearInterval(adminLoginPollRef.current);
          adminLoginPollRef.current = null;
          setAdminLoading(false);
        }
      } catch (err) {
        console.error("Admin login refresh error:", err);
      }
    }, 2000);
  };

  const logoutClient = async (clientId) => {
    setLoadingClients(prev => ({ ...prev, [clientId]: true }));

    try {
      const res = await fetch(`${API_BASE}/client/${clientId}/logout`, {
        method: "POST",
      });
      const data = await res.json();

      if (!res.ok) {
        alert(
          data.detail
          || "This client has open positions. Please close them before logging out."
        );
        return;
      }

      await refreshClientsAndLogin();
    } catch (err) {
      console.error("Client logout error:", err);
      alert("Client logout failed");
    } finally {
      setLoadingClients(prev => ({ ...prev, [clientId]: false }));
    }
  };

  const startClientLoginPoll = (clientId) => {
    if (loginPollRef.current[clientId]) {
      clearInterval(loginPollRef.current[clientId]);
    }

    const startedAt = Date.now();

    loginPollRef.current[clientId] = setInterval(async () => {
      try {
        const status = await refreshClientsAndLogin();
        const isLoggedIn = Boolean(status[clientId]);
        const timedOut = Date.now() - startedAt > 180000;

        if (isLoggedIn || timedOut) {
          clearInterval(loginPollRef.current[clientId]);
          delete loginPollRef.current[clientId];
          setLoadingClients(prev => ({ ...prev, [clientId]: false }));
        }
      } catch (err) {
        console.error("Client login refresh error:", err);
      }
    }, 2000);
  };

  const squareOff = async () => {
    try {
      await fetch(`${API_BASE}/algo/square-off`, { method: "POST" });
    } catch {
      alert("Square off failed");
    }
  };
  
  // ---------------- WS CONNECTION ----------------
  const connectWS = useCallback(() => {
    if (wsRef.current) return;

    wsIntentionalCloseRef.current = false;
    const ws = new WebSocket(getWsUrl());
    wsRef.current = ws;
    lastWsMessageAtRef.current = Date.now();

    ws.onmessage = (event) => {
      lastWsMessageAtRef.current = Date.now();
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        console.error("Invalid WS data:", event.data);
        return;
      }

      if (data.type === "dashboard") {
        setWsStatus("CONNECTED");
        setDashboard({ 
          clients: data.clients || [], 
          positions: data.positions || [], 
          orders: data.orders || [], 
          auto_stock: data.auto_stock || { running: false, stocks: [] },
          options: data.options || [], 
          atm: data.atm, 
          expiry: data.expiry,
          spot: data.spot,
          index: data.index,
          trade_symbols: data.trade_symbols || [],
          active_trade_symbol: data.active_trade_symbol || data.index,
          chain_generation: data.chain_generation || 0,
          chain_loading: Boolean(data.chain_loading),
          trade_lock: data.trade_lock || { locked: false, allowed: true, symbols: [] },
          copy_trade_trail: data.copy_trade_trail || { active: false, status: "IDLE" }
        });

        if(data.algo){
          setAlgoData(data.algo);
        }
      }       
    };

    ws.onclose = async () => {
      if (wsRef.current !== ws) return;
      wsRef.current = null;
      if (wsIntentionalCloseRef.current || wsAuthCheckRef.current) return;

      wsAuthCheckRef.current = true;
      try {
        const response = await fetch(`${API_BASE}/admin/auth/me`, {
          cache: "no-store",
          credentials: "same-origin"
        });
        const auth = await response.json();
        if (!response.ok || !auth.authenticated) {
          setWsStatus("SESSION_EXPIRED");
          onSessionExpired("Session expired. Please log in again.");
          return;
        }
        setWsStatus("RECONNECTING");
      } catch (error) {
        console.error("Unable to verify dashboard session", error);
        setWsStatus("OFFLINE");
      } finally {
        wsAuthCheckRef.current = false;
      }

      if (systemReadyRef.current) {
        clearTimeout(wsReconnectTimerRef.current);
        wsReconnectTimerRef.current = setTimeout(connectWS, 2000);
      }
    };

    ws.onerror = (err) => {
      console.error("WebSocket error", err);
      ws.close();
    };

    return ws;
  }, [onSessionExpired]);
  
  useEffect(() => {
    const symbols = dashboard.trade_symbols || [];
    if (symbols.length && !symbols.includes(selectedTradeSymbol)) {
      setSelectedTradeSymbol(
        dashboard.active_trade_symbol || symbols[0]
      );
    }
  }, [
    dashboard.trade_symbols,
    dashboard.active_trade_symbol,
    selectedTradeSymbol
  ]);

  useEffect(() => {
    if (!systemReady || !selectedTradeSymbol) return;

    const controller = new AbortController();
    const selectChain = async () => {
      try {
        setChainSwitching(true);
        setSelectedOption(null);
        setDashboard(prev => ({
          ...prev,
          options: [],
          atm: null,
          expiry: null,
          spot: null,
          chain_loading: true
        }));

        const res = await fetch(
          `${API_BASE}/copy-trade/chain/select`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              symbol: selectedTradeSymbol,
              expiry_type: expiryType
            }),
            signal: controller.signal
          }
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.detail || "Option chain switch failed");
        }

        setDashboard(prev => ({
          ...prev,
          options: data.chain || [],
          atm: data.atm || null,
          expiry: data.expiry || null,
          spot: data.spot || null,
          active_trade_symbol: data.symbol,
          trade_symbols: data.symbols || prev.trade_symbols,
          chain_generation: data.generation || 0,
          chain_loading: data.status !== "ready",
          trade_lock: data.trade_lock || prev.trade_lock
        }));
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Copy Trade chain switch failed:", err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setChainSwitching(false);
        }
      }
    };

    selectChain();
    return () => controller.abort();
  }, [API_BASE, expiryType, selectedTradeSymbol, systemReady]);

  useEffect(() => {
    const listener = (event) => {
      if (
        event.data === "LOGIN_SUCCESS"
        || event.data === "LOGIN_PARTIAL"
      ) {
        refreshClientsAndLogin(); // refresh UI        
      }
    };

    window.addEventListener("message", listener);

    return () => window.removeEventListener("message", listener);
  }, [refreshClientsAndLogin]);

  useEffect(() => {
    if (loginStatus.admin && !loginStatus.init_in_progress) {
      setAdminLoading(false);
    }
  }, [loginStatus.admin, loginStatus.init_in_progress]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadAdminAccounts();
  }, [loadAdminAccounts]);

  // ---------------- WS INIT ----------------
  useEffect(() => {
    if (!systemReady) return;

    connectWS();
    
    return () => {
      wsIntentionalCloseRef.current = true;
      clearTimeout(wsReconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [systemReady, connectWS]);

  useEffect(() => {
    if (!systemReady) return;

    const interval = setInterval(() => {
      const ws = wsRef.current;
      const stale = Date.now() - lastWsMessageAtRef.current > 10000;

      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        wsRef.current = null;
        connectWS();
        return;
      }

      if (stale && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [systemReady, connectWS]);
  
  useEffect(() => {
    if(!systemReady) return;

    refreshClientsAndLogin();
    
    const interval = setInterval(() => {      
      refreshClientsAndLogin();
    }, 2000);

    return () => {      
      clearInterval(interval);
    };
  }, [systemReady, refreshClientsAndLogin]);

  useEffect(() => {
    return () => {
      Object.values(loginPollRef.current).forEach(clearInterval);
      loginPollRef.current = {};
      if (adminLoginPollRef.current) {
        clearInterval(adminLoginPollRef.current);
        adminLoginPollRef.current = null;
      }
    };
  }, []);

  const updateClientAlgoProduct = useCallback(async (clientId, product, enabled) => {
    setLoadingClients(prev => ({ ...prev, [clientId]: true }));
    try {
      const response = await fetch(
        `${API_BASE}/clients/${encodeURIComponent(clientId)}/algo-products`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ [product]: enabled }),
        },
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Unable to update ALGO selection");
      await loadClients();
    } catch (error) {
      alert(error.message);
    } finally {
      setLoadingClients(prev => ({ ...prev, [clientId]: false }));
    }
  }, [loadClients]);

  const updateClientIndexLots = useCallback(async (clientId, instrument, lots) => {
    setLoadingClients(prev => ({ ...prev, [clientId]: true }));
    try {
      const response = await fetch(`${API_BASE}/clients/${encodeURIComponent(clientId)}/index-lots`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instrument, lots }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Unable to update index lots");
      await loadClients();
    } catch (error) { alert(error.message); }
    finally { setLoadingClients(prev => ({ ...prev, [clientId]: false })); }
  }, [loadClients]);

  const updateClientAutoStock = useCallback(async (clientId, enabled) => {
    setLoadingClients(prev => ({ ...prev, [clientId]: true }));
    try {
      const response = await fetch(`${API_BASE}/clients/${encodeURIComponent(clientId)}/auto-stock`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Update failed");
      await loadClients();
    } catch (error) { alert(error.message); }
    finally { setLoadingClients(prev => ({ ...prev, [clientId]: false })); }
  }, [loadClients]);

  const updateClientAutoStockCapital = useCallback(async (clientId, amount) => {
    setLoadingClients(prev => ({ ...prev, [clientId]: true }));
    try {
      const response = await fetch(`${API_BASE}/clients/${encodeURIComponent(clientId)}/auto-stock-capital`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Unable to update stock allocation");
      await loadClients();
      return true;
    } catch (error) { alert(error.message); return false; }
    finally { setLoadingClients(prev => ({ ...prev, [clientId]: false })); }
  }, [loadClients]);

  const updateClientCapital = useCallback(async (clientId, capital) => {
    setLoadingClients(prev => ({ ...prev, [clientId]: true }));
    try {
      const response = await fetch(`${API_BASE}/clients/${encodeURIComponent(clientId)}/capital`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ capital }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Unable to update capital");
      await loadClients();
      return true;
    } catch (error) { alert(error.message); return false; }
    finally { setLoadingClients(prev => ({ ...prev, [clientId]: false })); }
  }, [loadClients]);

  const resetClientCapital = useCallback(async clientId => {
    setLoadingClients(prev => ({ ...prev, [clientId]: true }));
    try {
      const response = await fetch(`${API_BASE}/clients/${encodeURIComponent(clientId)}/capital`, {
        method: "DELETE",
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Unable to reset capital");
      await loadClients();
      return true;
    } catch (error) { alert(error.message); return false; }
    finally { setLoadingClients(prev => ({ ...prev, [clientId]: false })); }
  }, [loadClients]);

  // ---------------- ATM AUTO SCROLL ---------------- 
  useEffect(() => { 
    const el = document.querySelector(".atm-row"); 
    if (el) { 
      el.scrollIntoView({ block: "center" }); 
    } 
  }, [dashboard.atm]);

  // ---------------- Common for labels font ----------------
  const labelStyle = {
    fontSize: "12px",
    color: "#9ca3af",
    letterSpacing: "0.5px",
    textTransform: "uppercase"
  };

  const valueStyle = {
    fontSize: "18px",
    fontWeight: "600",
    color: "#e5e7eb"
  };

  const activeStyle = (isActive) => ({
    fontSize: "13px",
    fontWeight: "600",
    color: isActive ? "#16c784" : "#ea3943"
  });

  const cardStyle = {
    padding: "10px 12px",
    borderRadius: "10px",
    background: "#0b0f1a",
    border: "1px solid #1f2937",
    marginBottom: "10px"
  };

  const downloadBtnStyle = {
    background: "#111827",
    color: "#e5e7eb",
    border: "1px solid #374151",
    borderRadius: "6px",
    padding: "6px 8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px"
  };
  
  const algoOrders = dashboard.orders.filter(o => o.source === "ALGO");
  const copyOrders = dashboard.orders.filter(o => o.source === "TRADE");
  const autoStockOrders = dashboard.orders.filter(o => o.source === "AUTO_STOCK");

  const algoPositions = dashboard.positions.filter(p => p.source === "ALGO");
  const copyPositions = dashboard.positions.filter(p => p.source === "TRADE");
  const autoStockPositions = dashboard.positions.filter(p => p.source === "AUTO_STOCK");
  const openCopyPositions = copyPositions.filter(
    p => Number(p.qty || 0) !== 0 && !p.closed
  );
  const copyAdminPositions = copyPositions.filter(
    p => p.client_id === "admin"
  );
  const copyClientPositions = copyPositions.filter(
    p => p.client_id !== "admin"
  );
  const openCopyClientPositions = openCopyPositions.filter(
    p => p.client_id !== "admin"
  );
  const copyPositionClients = new Set(
    openCopyClientPositions.map(p => p.client_id)
  ).size;
  const copyOpenQuantity = openCopyClientPositions.reduce(
    (total, p) => total + Math.abs(Number(p.qty || 0)),
    0
  );
  const copyLivePnl = openCopyClientPositions.reduce(
    (total, p) => total + Number(p.trade_total_pnl ?? p.pnl ?? 0),
    0
  );
  const staleCopyPositions = openCopyPositions.filter(
    p => p.market_data_stale
  ).length;

  const algoAdminPositions = algoPositions.filter(p => p.client_id === "admin");
  const algoClientPositions = algoPositions.filter(p => p.client_id !== "admin");

  const orderPanelRef = useRef(null);
  useEffect(() => {
    if (selectedOption && orderPanelRef.current) {
      orderPanelRef.current.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  }, [selectedOption]);

  const activeLastRun = algoData.compare_last_run?.[activeCompareSymbol];
  const isCompareRunning = algoData.compare_running === true;
  const marketConnected = systemStatus.market_data?.connected === true;
  const defaultIpGroup = systemStatus.execution?.default_ip_group || "IP_GROUP_01";
  const brokerBatches = systemStatus.execution?.broker_batches || {};
  const enabledBatchCount = Object.values(brokerBatches)
    .filter((b) => b?.enabled !== false)
    .length;
  const isSystemHealthy = systemReady && loginStatus.admin && marketConnected;
  const activeClients = dashboard.clients.filter((c) => c.active === true);
  const onlineClients = activeClients.filter((c) => c.logged_in ?? loginStatus[c.id]);
  const readyClients = activeClients.filter((c) => {
    const isOnline = c.logged_in ?? loginStatus[c.id];
    const apiStatus = String(c.api_health || "UNKNOWN").toUpperCase();
    const syncStatus = String(c.position_sync_status || "NOT_STARTED").toUpperCase();
    const hasCapital = Number(c.available_capital ?? c.capital ?? 0) > 0;

    return (
      isOnline &&
      ["OK", "SYNCED", "PAPER_MODE", "UNKNOWN"].includes(apiStatus) &&
      ["OK", "SYNCED", "PAPER_MODE", "NO_MINTX_POSITIONS", "NOT_STARTED", "UNKNOWN"].includes(syncStatus) &&
      hasCapital
    );
  });
  const apiWarnings = activeClients.filter((c) => {
    const status = String(c.api_health || "UNKNOWN").toUpperCase();
    return !["OK", "SYNCED", "PAPER_MODE"].includes(status);
  });
  const syncWarnings = activeClients.filter((c) => {
    const status = String(c.position_sync_status || "NOT_STARTED").toUpperCase();
    return !["OK", "SYNCED", "NO_MINTX_POSITIONS"].includes(status);
  });
  const marginWarnings = activeClients.filter((c) => Number(c.broker_capital || 0) <= 0);
  const rejectedOrders = activeClients.reduce(
    (total, c) => total + Number(c.rejected_orders || 0),
    0
  );
  const pendingOrderStatuses = new Set(["QUEUED", "PROCESSING", "SUBMITTED", "RECONCILIATION_REQUIRED"]);
  const failedOrderStatuses = new Set(["FAILED", "REJECTED", "CANCELLED"]);
  const pendingOrders = dashboard.orders.filter(order => pendingOrderStatuses.has(String(order.status || "").toUpperCase()));
  const visibleFailedOrders = dashboard.orders.filter(order => failedOrderStatuses.has(String(order.status || "").toUpperCase()));
  const visibleCompletedOrders = dashboard.orders.filter(order => ["COMPLETED", "PARTIAL_SUCCESS"].includes(String(order.status || "").toUpperCase()));
  const oldestPendingAt = pendingOrders.reduce((oldest, order) => {
    const value = order.processing_at || order.created_at || order.updated_at;
    return value && (!oldest || String(value) < String(oldest)) ? value : oldest;
  }, null);
  const runtimeWarnings = activeClients.map(client => {
    const isOnline = Boolean(client.logged_in ?? loginStatus[client.id]);
    const apiStatus = String(client.api_health || "UNKNOWN").toUpperCase();
    const syncStatus = String(client.position_sync_status || "NOT_STARTED").toUpperCase();
    const rejected = Number(client.rejected_orders || 0);
    const latestOrder = dashboard.orders.find(order => String(order.order_id || order.id || "") === String(client.last_order_id || ""));
    const issues = [];
    if (!isOnline) issues.push("Client session offline");
    if (!['OK', 'SYNCED', 'PAPER_MODE'].includes(apiStatus)) issues.push(`API: ${apiStatus.replaceAll("_", " ")}`);
    if (!['OK', 'SYNCED', 'NO_MINTX_POSITIONS'].includes(syncStatus)) issues.push(`Position sync: ${syncStatus.replaceAll("_", " ")}`);
    if (Number(client.broker_capital || 0) <= 0) issues.push("Broker capital unavailable");
    if (rejected) issues.push(`${rejected} rejected order${rejected === 1 ? "" : "s"}`);
    const errors = [...new Set([
      client.last_order_error,
      client.position_sync_error,
      client.last_login_error,
    ].filter(Boolean).map(String))];
    return {
      client,
      issues,
      errors,
      latestOrder,
      tone: rejected || !isOnline || Number(client.broker_capital || 0) <= 0 ? "bad" : "warn",
    };
  }).filter(item => item.issues.length || item.errors.length);
  const strategyClientsReady = systemReady && hasLoggedInTradingClient(dashboard.clients, loginStatus);
  const clientsReady = (
    systemReady &&
    readyClients.length > 0
  );
  const copyTradeChainUnavailable = (
    chainSwitching
    || dashboard.chain_loading
    || !dashboard.options.length
    || dashboard.active_trade_symbol !== selectedTradeSymbol
  );
  const copyTradeLocked = dashboard.trade_lock?.allowed === false;
  const copyTradeDisabled = (
    wsStatus !== "CONNECTED"
    || copyTradeChainUnavailable
    || copyTradeLocked
  );
  const copyTradeDisabledReason = copyTradeLocked
    ? dashboard.trade_lock?.reason
      || `Close the active ${dashboard.trade_lock?.symbols?.join(", ")} Copy Trade position first`
    : wsStatus !== "CONNECTED"
      ? "Live dashboard connection is unavailable. Trading is disabled until fresh data arrives."
    : copyTradeChainUnavailable
      ? `Loading fresh ${selectedTradeSymbol || "Copy Trade"} option chain`
      : "";

  useEffect(() => {
    if (loginStatus.admin && !adminRedirectedRef.current) {
      adminRedirectedRef.current = true;
      setActiveTab(strategyClientsReady ? "STOCK_OPTIONS" : "CLIENTS");
    }
  }, [loginStatus.admin, strategyClientsReady]);

  useEffect(() => {
    if (
      (!strategyClientsReady && ["ALGO", "STOCK_OPTIONS", "AUTO_STOCK", "EQUITY_SWING"].includes(activeTab))
      || (!clientsReady && ["COPY_TRADE", "TRADE_MANAGEMENT"].includes(activeTab))
    ) {
      setActiveTab("CLIENTS");
    }
  }, [activeTab, clientsReady, strategyClientsReady]);

  const HealthMetric = ({ label, value, tone = "muted" }) => (
    <div className={`health-metric ${tone}`}>
      <span><i className="health-severity" aria-hidden="true">{tone === "bad" ? "!" : tone === "warn" ? "▲" : tone === "ok" ? "✓" : "•"}</i>{label}</span>
      <strong>{value}</strong>
    </div>
  );

  const RuntimeWarning = ({ item }) => {
    const { client, issues, errors, latestOrder, tone } = item;
    const orderSource = latestOrder?.source === "ALGO" ? "Auto Index" : latestOrder?.source === "AUTO_STOCK" ? "Auto Stock" : latestOrder?.source;
    return <article className={`runtime-warning ${tone}`}>
      <header><strong>{client.name || client.id}</strong><span>{client.id}</span><em>{String(client.broker || "Broker").toUpperCase()}</em></header>
      <div className="runtime-warning-issues">{issues.map(issue => <span key={issue}>{issue}</span>)}</div>
      {errors.map((error, index) => <p key={`${error}-${index}`}>{error}</p>)}
      <footer>
        {latestOrder && <span>{orderSource || "Order"}{latestOrder.tradingsymbol ? ` · ${latestOrder.tradingsymbol}` : ""}</span>}
        {client.last_order_status && <span>Status: {String(client.last_order_status).replaceAll("_", " ")}</span>}
        {client.last_order_id && <span>Order: {client.last_order_id}</span>}
        {client.last_order_at && <span>{fmtTime(client.last_order_at)}</span>}
        {client.last_order_latency_ms != null && <span>{Number(client.last_order_latency_ms).toLocaleString("en-IN")} ms</span>}
        <span>Route: {client.execution_ip_group || defaultIpGroup} · IP {client.source_ip || "Default"}</span>
      </footer>
    </article>;
  };

  const ClientHealthStrip = () => (
    <div className="client-health-strip">
      <HealthMetric label="Active" value={activeClients.length} tone="ok" />
      <HealthMetric label="Online" value={`${onlineClients.length}/${activeClients.length}`} tone="ok" />
      <HealthMetric label="Ready" value={readyClients.length} tone={readyClients.length ? "ok" : "warn"} />
      <HealthMetric
        label="API"
        value={apiWarnings.length ? `${apiWarnings.length} warn` : "OK"}
        tone={apiWarnings.length ? "warn" : "ok"}
      />
      <HealthMetric
        label="Sync"
        value={syncWarnings.length ? `${syncWarnings.length} warn` : "OK"}
        tone={syncWarnings.length ? "warn" : "ok"}
      />
      <HealthMetric
        label="Margin"
        value={marginWarnings.length ? `${marginWarnings.length} blocked` : "OK"}
        tone={marginWarnings.length ? "bad" : "ok"}
      />
      <HealthMetric
        label="Rejected"
        value={rejectedOrders}
        tone={rejectedOrders ? "bad" : "muted"}
      />
    </div>
  );

  const CopyTradeGist = ({ showManageButton = false }) => {
    const trailState = dashboard.copy_trade_trail || {};
    const trailLabel = trailState.active
      ? trailState.market_data_stale
        ? "Paused"
        : String(trailState.status || "Active")
      : "Off";
    const latestOrder = copyOrders[0];

    return (
      <div className="copy-trade-gist">
        <div className="client-health-strip">
          <HealthMetric
            label="Open Positions"
            value={openCopyPositions.length}
            tone={openCopyPositions.length ? "ok" : "muted"}
          />
          <HealthMetric
            label="Clients"
            value={copyPositionClients}
            tone={copyPositionClients ? "ok" : "muted"}
          />
          <HealthMetric
            label="Client Qty"
            value={copyOpenQuantity}
            tone={copyOpenQuantity ? "ok" : "muted"}
          />
          <HealthMetric
            label="Live P&L"
            value={`₹${copyLivePnl.toFixed(2)}`}
            tone={copyLivePnl > 0 ? "ok" : copyLivePnl < 0 ? "bad" : "muted"}
          />
          <HealthMetric
            label="Market Data"
            value={staleCopyPositions ? `${staleCopyPositions} stale` : "Fresh"}
            tone={staleCopyPositions ? "bad" : "ok"}
          />
          <HealthMetric
            label="Trail"
            value={trailLabel}
            tone={trailState.market_data_stale ? "warn" : trailState.active ? "ok" : "muted"}
          />
          <HealthMetric
            label="Latest Order"
            value={latestOrder?.status || "None"}
            tone={latestOrder?.status === "FAILED" ? "bad" : latestOrder ? "ok" : "muted"}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="terminal">

      {/* 🔥 TOP CONTROL BAR */}
      <div className="topbar">

        <div className="left">
          <img className="brand-logo" src="/brand/mintxpro-logo-cyan-emerald.png" alt="MintXPro" />
        </div>

        <div className="center">
          <span className={isSystemHealthy ? "status green" : "status red"}>
            {isSystemHealthy ? "System Ready" : "System Not Ready"}
          </span>
        </div>

        <div className="right">
          <TradeNotificationCenter
            hydrated={wsStatus === "CONNECTED"}
            orders={dashboard.orders}
            autoStock={dashboard.auto_stock}
            indexInstances={activeFamilyInstances}
            onNavigate={type => setActiveTab(type === "AUTO_STOCK" ? "AUTO_STOCK" : "ALGO")}
          />
          <select
            className="admin-selector"
            value={selectedAdminId}
            onChange={(event) => setSelectedAdminId(event.target.value)}
            disabled={adminLoading || (loginStatus.admin && systemReady) || adminAccounts.length <= 1}
            title={loginStatus.admin && systemReady ? "An admin is already active" : "Select admin account"}
          >
            {adminAccounts.length === 0 && (
              <option value="">Admin</option>
            )}
            {adminAccounts.map((admin) => (
              <option key={admin.id} value={admin.id}>
                {admin.name || admin.id}
              </option>
            ))}
          </select>

          <button
            onClick={loginAdmin}
            disabled={adminLoading || (loginStatus.admin && systemReady)}
            title={loginStatus.initialization_error || ""}
          >
            {adminLoading
              ? "Admin Initializing..."
              : loginStatus.admin && systemReady
                ? "Admin LoggedIn"
                : loginStatus.admin
                  ? "Retry System Initialization"
                  : "Admin Login"}
          </button>

          {loginStatus.initialization_error && (
            <span style={{ color: "#fca5a5", fontSize: 12 }}>
              {loginStatus.initialization_error}
            </span>
          )}

          <button onClick={onLogout}>
            Logout
          </button>          
        </div>              
      </div>

      {wsStatus !== "CONNECTED" && (
        <div
          className={`connection-banner ${wsStatus.toLowerCase()}`}
          role="status"
        >
          <strong>
            {wsStatus === "SESSION_EXPIRED"
              ? "Session expired"
              : wsStatus === "OFFLINE"
                ? "Server unavailable"
                : "Live data disconnected"}
          </strong>
          <span>
            {wsStatus === "SESSION_EXPIRED"
              ? "Please log in again."
              : "Showing the last received data. Trading is disabled while MintX reconnects."}
          </span>
        </div>
      )}

      {/* Primary workspaces. Copy Trade execution remains available internally, but its tab is intentionally hidden. */}
      <div className="workspace-shell">
      <WorkspaceNavigation activeTab={activeTab} onSelect={setActiveTab} clientsReady={strategyClientsReady} equityTab={equityTab} connection={wsStatus} />
      <div className="workspace-content">
      {["STOCK_OPTIONS", "AUTO_STOCK", "EQUITY_SWING"].includes(activeTab) && <nav className="strategy-subtabs" aria-label="Equity strategies">{[["STOCK_OPTIONS", "Options"], ["AUTO_STOCK", "Intraday"], ["EQUITY_SWING", "Swing"]].map(([tab, label]) => <button key={tab} type="button" aria-pressed={activeTab === tab} className={activeTab === tab ? "active" : ""} disabled={!strategyClientsReady} onClick={() => { setEquityTab(tab); setActiveTab(tab); }}>{label}</button>)}</nav>}
      {/* 🔥 MAIN TRADE GRID */}
      {activeTab === "POSITIONS" && <TradingPositions
        instances={Object.keys(algoData.families || {}).length ? Object.fromEntries(Object.entries(algoData.families).flatMap(([family, data]) => Object.entries(data.instances || {}).map(([instrument, state]) => [`${family}:${instrument}`, { ...state, instrument, strategy_family: family }]))) : selectedFamilyInstances}
        positions={dashboard.positions} orders={dashboard.orders} stock={dashboard.auto_stock} clients={dashboard.clients} connected={wsStatus === "CONNECTED"}
      />}
      {strategyClientsReady && activeTab === "STOCK_OPTIONS" && <StockOptions onClientAccounts={() => setActiveTab("CLIENTS")} centralizedPositions onPositions={() => setActiveTab("POSITIONS")} />}
      {strategyClientsReady && activeTab === "EQUITY_SWING" && <EquitySwing onClientAccounts={() => setActiveTab("CLIENTS")} onPositions={() => setActiveTab("POSITIONS")} />}
      <div style={{ display: activeTab === "PERFORMANCE" ? "block" : "none" }}>
        <PerformanceDashboard clients={dashboard.clients} onSessionExpired={onSessionExpired} active={activeTab === "PERFORMANCE"} connected={wsStatus === "CONNECTED"}
          instances={Object.keys(algoData.families || {}).length ? Object.fromEntries(Object.entries(algoData.families).flatMap(([family, data]) => Object.entries(data.instances || {}).map(([instrument, state]) => [`${family}:${instrument}`, { ...state, instrument, strategy_family: family }]))) : selectedFamilyInstances}
          positions={dashboard.positions} orders={dashboard.orders} stock={dashboard.auto_stock} />
      </div>
      {strategyClientsReady && activeTab === "AUTO_STOCK" && <div>
        <AutoStockTrade
          key={activeTab}
          mode="paper"
          centralizedPositions
          onClientAccounts={() => setActiveTab("CLIENTS")}
          onPositions={() => setActiveTab("POSITIONS")}
          orders={autoStockOrders}
          positions={autoStockPositions}
          clients={dashboard.clients}
        />
      </div>}
      {activeTab === "AUTO_INDEX_BACKTEST" && <AutoIndexBacktest />}
      {activeTab === "TREND_HEALTH" && <TrendHealthPage instances={algoData.families?.NIFTY_EMA_FLIP?.instances || {}} />}
      <div style={{ display: activeTab === "COPY_TRADE" ? "block" : "none" }}>
        <div className="copy-symbol-tabs">
          {(dashboard.trade_symbols || []).map(symbol => (
            <button
              key={symbol}
              className={selectedTradeSymbol === symbol ? "active" : ""}
              onClick={() => {
                setSelectedOption(null);
                setSelectedTradeSymbol(symbol);
              }}
            >
              {symbol}
            </button>
          ))}
          {copyTradeLocked && (
            <span className="copy-symbol-lock">
              Trading locked to {dashboard.trade_lock?.symbols?.join(", ")}
            </span>
          )}
        </div>
        <div className="main-grid">

          {/* LEFT - OPTIONS (MAIN FOCUS) */}
            <div className="panel options-panel">
              <div className="panel-title">
                {selectedTradeSymbol || "Copy Trade"} Options Chain
                {copyTradeChainUnavailable ? " · Loading" : ""}
              </div>
              <div className="panel-body">
                <OptionsChain
                  options={dashboard.options}
                  atm={dashboard.atm}
                  spot={dashboard.spot}
                  expiry={dashboard.expiry}
                  onSelect={setSelectedOption}
                  selectedOption={selectedOption}              
                  onExpiryChange={setExpiryType}
                />
              </div>  
            </div>

          {/* RIGHT STACK */}
          <div className="control-stack">

            <div className="panel order-entry-panel">
              <div className="panel-title">Order Entry</div>
              <div className="panel-body order-entry-body" ref={orderPanelRef}>
                <OrderPanel
                  option={selectedOption}
                  copyTradeSymbol={selectedTradeSymbol}
                  chainGeneration={dashboard.chain_generation}
                  tradeDisabled={copyTradeDisabled}
                  tradeDisabledReason={copyTradeDisabledReason}
                />
              </div>  
            </div>
            
            <div className="panel client-summary-panel">
              <div className="panel-title">Client Summary</div>
              <div className="panel-body client-summary-body">
                <ClientHealthStrip />
              </div>
            </div>
          </div>

          <div className="panel copy-trade-gist-panel">
            <div className="panel-title">Live Trade Summary</div>
            <div className="panel-body">
              <CopyTradeGist />
            </div>
          </div>
        </div>
      </div>
      {activeTab === "AI" && <section aria-label="AI">
        <div className="positions-category-tabs">
          {[["HYBRID_AI", "Hybrid AI Trading (PAPER)"], ["EXIT_ASSISTANT", "Exit Assistance (PAPER)"], ["LIBRARY", "Learning Library"]].map(([id, label]) =>
            <button key={id} aria-pressed={aiView === id} onClick={() => setAiView(id)}>{label}</button>)}
        </div>
        {aiView === "LIBRARY" ? <AILearningLibrary /> : aiView === "EXIT_ASSISTANT" ? <ExitAssistant /> :
          <HybridAI onPositions={() => setActiveTab("POSITIONS")} onClientAccounts={() => setActiveTab("CLIENTS")} />}
      </section>}
      {/* 🔥 MAIN ALGO GRID */}
      {strategyClientsReady && <div style={{ display: activeTab === "ALGO" ? "block" : "none" }}>
        <div className="positions-category-tabs"><button aria-pressed={indexView === "EMA"} onClick={() => setIndexView("EMA")}>EMA Flip</button><button aria-pressed={indexView === "EMA21"} onClick={() => setIndexView("EMA21")}>NIFTY EMA21 Breakout (PAPER)</button></div>
        {indexView === "EMA21" ? <IndexEMA21 onPositions={() => setActiveTab("POSITIONS")} onClientAccounts={() => setActiveTab("CLIENTS")} /> : productionAlgoSymbols.length > 0 ? <AutoIndexTrade clients={dashboard.clients} onClientAccounts={() => setActiveTab("CLIENTS")} onPositions={() => setActiveTab("POSITIONS")} strategyFamily={activeAlgoFamily} symbols={productionAlgoSymbols} instances={selectedFamilyInstances} positions={dashboard.positions} orders={dashboard.orders} grossSummary={algoData.auto_index_gross} onStartIndex={startAlgoRequest} onStopIndex={stopAlgoRequest} /> : <div className="panel"><div className="panel-body algo-empty"><strong>Auto Index unavailable</strong><span>NIFTY and SENSEX strategy services are not ready.</span></div></div>}
      </div>}

      <div style={{ display: "none" }}>
        <div className="algo-workspace">

          {/* 🔥 TOP ROW: Strategy + Clients side by side */}
          <div
            className="algo-top-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.45fr) minmax(360px, 0.75fr)",
              gap: 12,
              alignItems: "start"
            }}
          >  
            {/* 🔥 LEFT - STRATEGY PANEL */}
            <div className="panel" style={{ alignSelf: "start" }}>
              <div className="panel-title">Sensex Strategy</div>

              {/* 🔹 Status */}
              <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 16px",
                    borderRadius: "10px",
                    background: "#0b0f1a",
                    border: "1px solid #1f2937",
                    marginBottom: "10px"
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4}}>
                  
                  {/* 🔹 LABEL */}
                  <span style={{
                          fontSize: "11px",
                          color: "#9ca3af",
                          letterSpacing: "0.6px",
                          textTransform: "uppercase"
                        }}>
                    Strategy Status
                  </span>

                  {/* 🔹 VALUE */}
                  <span style={{ fontSize: "16px", fontWeight: "600",
                          color: algoData.running ? "#16c784" : "#ea3943"}}>

                    {algoData.running ? "🟢 RUNNING" : "🔴 STOPPED"}
                  </span>
                </div>

                <div style={{ textAlign: "right"}}>
                  {/* 🔹 MODE */}
                  <span style={{
                    fontSize: "11px",
                    color: "#9ca3af",
                    letterSpacing: "0.6px",
                    textTransform: "uppercase"
                  }}>
                    Mode
                  </span>

                  {/* 🔹 VALUE */}
                  <div style={{
                    marginTop: "2px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: algoData.mode === "AGGRESSIVE" ? "#f59e0b" : "#16c784"
                  }}>
                    {algoData.mode === "AGGRESSIVE" ? "⚡ AGGRESSIVE" : "▶ NORMAL"}
                  </div>
                </div>
              </div>

              {/* 🔹 Controls */}
              <div className="algo-action-grid" style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <button onClick={() => startAlgo("NORMAL")} 
                        disabled={algoData.running}
                        style={{
                          flex: 1,
                          background: "#16c784",
                          color: "#ffffff",
                          border: "none",
                          padding: "12px",
                          borderRadius: "8px",
                          fontWeight: "bold",
                          cursor: !algoData.running? "pointer":"not-allowed",
                          opacity: algoData.running ? 0.6 : 1,}}>
                  ▶ Start Normal
                </button>

                <button onClick={() => startAlgo("AGGRESSIVE")} 
                        disabled={algoData.running}
                        style={{
                          flex: 1,
                          background: "#f59e0b",
                          color: "#000000",
                          border: "none",
                          padding: "12px",
                          borderRadius: "8px",
                          fontWeight: "bold",
                          cursor: !algoData.running? "pointer":"not-allowed",
                          opacity: algoData.running ? 0.6 : 1,}}>
                  ⚡Start Aggressive
                </button>

                <button onClick={() => stopAlgo()} 
                        disabled={!algoData.running}
                        style={{
                          flex: 1,
                          background: "#ea3943",
                          color: "#ffffff",
                          border: "none",
                          padding: "12px",
                          borderRadius: "8px",
                          fontWeight: "bold",
                          cursor: algoData.running ? "pointer" : "not-allowed",
                          opacity: algoData.running ? 1 : 0.5,
                        }}>
                  ⏹ Stop
                </button>

                <button onClick={() => squareOff("ALL")}
                        disabled={!(algoData.running && (algoData.ce_active || algoData.pe_active))}
                        style={{
                          flex: 1,
                          background: "#fbbf24",
                          color: "#000000",
                          border: "none",
                          padding: "12px",
                          borderRadius: "8px",
                          fontWeight: "bold",
                          cursor: (algoData.ce_active || algoData.pe_active) ? "pointer" : "not-allowed",
                          opacity: (algoData.ce_active || algoData.pe_active) ? 1 : 0.5,
                        }}>
                  🧨 Exit All
                </button>

                <button onClick={() => fetch(`${API_BASE}/algo/square-off/ce`, { method: "POST" })}
                        disabled={!(algoData.running && algoData.ce_active)}
                        style={{
                          flex: 1,
                          background: "#3b82f6",
                          color: "#ffffff",
                          border: "none",
                          padding: "12px",
                          borderRadius: "8px",
                          fontWeight: "bold",
                          cursor: algoData.ce_active ? "pointer" : "not-allowed",
                          opacity: algoData.ce_active ? 1 : 0.5,
                        }}>
                  📈 Exit CE
                </button>

                <button onClick={() => fetch(`${API_BASE}/algo/square-off/pe`, { method: "POST" })}
                        disabled={!(algoData.running && algoData.pe_active)}
                        style={{
                          flex: 1,
                          background: "#8b5cf6",
                          color: "#ffffff",
                          border: "none",
                          padding: "12px",
                          borderRadius: "8px",
                          fontWeight: "bold",
                          cursor: algoData.pe_active ? "pointer" : "not-allowed",
                          opacity: algoData.pe_active ? 1 : 0.5,
                        }}>
                  📉 Exit PE
                </button>
              </div>

              <div style={cardStyle}>
  
                {/* 🔹 TOP ROW: LABEL + SYMBOL + BASE */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  
                  <div style={{ display: "flex"}}>
                    <span style={labelStyle}>CE LEG</span>
                  </div>

                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <span style={labelStyle}>
                      {algoData.ce_symbol ?? "-"}
                    </span>

                    <span style={labelStyle}>
                      Base: {algoData.ce_base ?? "-"}
                    </span>
                  </div>
                  <div style={{ minWidth: "120px", textAlign: "right" }}>
                    <span style={activeStyle(algoData.ce_active)}>
                      {algoData.ce_active ? "🟢 ACTIVE" : "🔴 INACTIVE"}
                    </span>
                  </div>
                </div>

                {/* 🔹 LTP + ARROW */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
                  
                  <span style={valueStyle}>
                    ₹ {algoData.ce_ltp ?? "-"}
                  </span>

                  {/* 🔥 Arrow */}
                  <span style={{
                    color: (algoData.ce_ltp - algoData.ce_base) >= 0 ? "#16c784" : "#ea3943",
                    fontSize: "14px"
                  }}>
                    {(algoData.ce_ltp - algoData.ce_base) >= 0 ? "▲" : "▼"}
                  </span>
                </div>

                {/* 🔹 ENTRY + DELTA */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
                  {/* 🔹 ENTRY */}
                  <span style={labelStyle}>
                    Entry: {algoData.ce_entry ?? "-"}
                  </span>
                  <span style={{
                    fontSize: "12px",
                    color: (algoData.ce_ltp - algoData.ce_entry) >= 0 ? "#16c784" : "#ea3943"
                  }}>
                    Δ {algoData.ce_entry ? (algoData.ce_ltp - algoData.ce_entry).toFixed(2) : "-"}
                  </span>
                </div>

              </div>

              <div style={cardStyle}>
  
                {/* 🔹 TOP ROW: LABEL + SYMBOL + BASE */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  
                  <div style={{ display: "flex"}}>
                    <span style={labelStyle}>PE LEG</span>
                  </div>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <span style={labelStyle}>
                      {algoData.pe_symbol ?? "-"}
                    </span>

                    <span style={labelStyle}>
                      Base: {algoData.pe_base ?? "-"}
                    </span>
                  </div>
                  <div style={{ minWidth: "120px", textAlign: "right" }}>
                    <span style={activeStyle(algoData.pe_active)}>
                      {algoData.pe_active ? "🟢 ACTIVE" : "🔴 INACTIVE"}
                    </span>
                  </div>
                </div>

                {/* 🔹 LTP + ARROW */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
                  
                  <span style={valueStyle}>
                    ₹ {algoData.pe_ltp ?? "-"}
                  </span>

                  {/* 🔥 Arrow */}
                  <span style={{
                    color: (algoData.pe_ltp - algoData.pe_base) >= 0 ? "#16c784" : "#ea3943",
                    fontSize: "14px"
                  }}>
                    {(algoData.pe_ltp - algoData.pe_base) >= 0 ? "▲" : "▼"}
                  </span>
                </div>

                {/* 🔹 ENTRY + DELTA */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
                  {/* 🔹 ENTRY */}
                  <span style={labelStyle}>
                    Entry: {algoData.pe_entry ?? "-"}
                  </span>
                  <span style={{
                    fontSize: "12px",
                    color: (algoData.pe_ltp - algoData.pe_entry) >= 0 ? "#16c784" : "#ea3943"
                  }}>
                    Δ {algoData.pe_entry ? (algoData.pe_ltp - algoData.pe_entry).toFixed(2) : "-"}
                  </span>
                </div>

              </div>

              {/* 🔹 PnL */}
              <div style={cardStyle}>
                <div style={labelStyle}>Total PnL</div>

                <div style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: algoData.pnl >= 0 ? "#16c784" : "#ea3943",
                  letterSpacing: "0.5px"
                }}>
                  ₹ {algoData.pnl ?? 0}
                </div>
              </div>
            </div>
            {/* RIGHT: Clients */}
            <div className="panel" style={{ alignSelf: "start" }}>
              <div className="panel-title">Client Summary</div>
              <div className="panel-body">
                <ClientHealthStrip />
              </div>
            </div>
          </div>
          {/* 🔥 ALGO COMPARE TABLE */}
          <div className="panel">
            <div className="panel-title">
              Algo1 / Algo1.1 / Algo1.2 Comparison - Shadow Mode
            </div>

            <div className="panel-body">
              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <button
                  onClick={startAlgoCompare}
                  disabled={isCompareRunning }
                  style={{
                    background: "#16c784",
                    color: "#ffffff",
                    border: "none",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    fontWeight: "bold",
                    cursor: !isCompareRunning  ? "pointer" : "not-allowed",
                    opacity: isCompareRunning  ? 0.6 : 1
                  }}
                >
                  ▶ Start Daily Compare
                </button>

                <button
                  onClick={stopAlgoCompare}
                  disabled={!isCompareRunning }
                  style={{
                    background: "#ea3943",
                    color: "#ffffff",
                    border: "none",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    fontWeight: "bold",
                    cursor: isCompareRunning  ? "pointer" : "not-allowed",
                    opacity: isCompareRunning  ? 1 : 0.5
                  }}
                >
                  ⏹ Stop Compare
                </button>

                <span style={{ ...labelStyle, alignSelf: "center" }}>
                  Viewing: {activeCompareSymbol} | Status:
                  {isCompareRunning  ? "RUNNING" : "STOPPED"}
                  {" | "}Rows: {getCompareRows().length}
                </span>
                {activeLastRun && (
                  <span style={{ ...labelStyle, alignSelf: "center", marginLeft: 12 }}>
                    Last Run: {activeLastRun.date}{" "}
                    {activeLastRun.time} | Winner:{" "}
                    {activeLastRun.winner || "-"} | Points:{" "}
                    {activeLastRun.winner_points ?? 0}
                  </span>
                )}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={labelStyle}>Algo Summary Cards</span>

                <button
                  style={downloadBtnStyle}
                  onClick={() =>
                    downloadExcel(
                      getCardSummaryRows(),
                      `${activeCompareSymbol}_algo_card_summary_${todayFileDate}.xlsx`,
                      "Card Summary"
                    )
                  }
                >
                  <Download size={14} /> Excel
                </button>
              </div>

              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                {compareSymbols.map((symbol) => (
                  <button
                    key={symbol}
                    onClick={() => setActiveCompareSymbol(symbol)}
                    style={{
                      padding: "8px 14px",
                      borderRadius: "8px",
                      border: activeCompareSymbol === symbol ? "1px solid #16c784" : "1px solid #374151",
                      background: activeCompareSymbol === symbol ? "#064e3b" : "#111827",
                      color: activeCompareSymbol === symbol ? "#ffffff" : "#9ca3af",
                      fontWeight: "700",
                      cursor: "pointer"
                    }}
                  >
                    {symbol}
                  </button>
                ))}
              </div>

              {getCompareRows().length === 0 && (
                <div
                  style={{
                    padding: 12,
                    border: "1px solid #374151",
                    borderRadius: 8,
                    color: "#f59e0b",
                    marginBottom: 12
                  }}
                >
                  No live compare data yet for {activeCompareSymbol}. Start Daily Compare or wait for backend update.
                </div>
              )}

              {/* 1️⃣ ALGO CARD SUMMARY */}
              <div className="algo-summary-grid" style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 10,
                marginBottom: 12
              }}>
                {getCompareRows().map((r) => (
                  <div key={r.name} style={cardStyle}>
                    <div style={{ ...labelStyle, marginBottom: 6 }}>{r.name}</div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                      <div>
                        <div style={labelStyle}>Flips</div>
                        <div style={valueStyle}>{r.flip_count ?? 0}</div>
                      </div>
                      <div>
                        <div style={labelStyle}>Entered</div>
                        <div style={{ ...valueStyle, color: "#16c784" }}>
                          {r.entered_flip_count ?? 0}
                        </div>
                      </div>
                      <div>
                        <div style={labelStyle}>Skipped</div>
                        <div style={{ ...valueStyle, color: "#f59e0b" }}>
                          {r.skipped_flip_count ?? 0}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: 8,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 6
                      }}
                    >
                      <span style={labelStyle}>Selection: {r.selection || "-"}</span>
                      <span style={labelStyle}>Delta: {r.delta ?? "-"}</span>
                      <span style={labelStyle}>IV: {r.iv ?? "-"}</span>
                    </div>

                    {r.name === "Algo1.1" && (
                    <div
                      style={{
                        marginTop: 10,
                        paddingTop: 8,
                        borderTop: "1px solid #1f2937",
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 8
                      }}
                    >
                      <div>
                        <div style={labelStyle}>Smart Exits</div>
                        <div style={valueStyle}>{r.early_exit_count ?? 0}</div>
                      </div>

                      <div>
                        <div style={labelStyle}>Good</div>
                        <div style={{ ...valueStyle, color: "#16c784" }}>
                          {r.good_early_exit_count ?? 0}
                        </div>
                      </div>

                      <div>
                        <div style={labelStyle}>False</div>
                        <div style={{ ...valueStyle, color: "#ea3943" }}>
                          {r.false_early_exit_count ?? 0}
                        </div>
                      </div>

                      <div>
                        <div style={labelStyle}>Accuracy</div>
                        <div style={valueStyle}>{r.early_exit_accuracy ?? 0}%</div>
                      </div>

                      <div>
                        <div style={labelStyle}>Avg Saved</div>
                        <div style={{ ...valueStyle, color: "#16c784" }}>
                          {r.avg_early_exit_points_saved ?? 0}
                        </div>
                      </div>

                      <div>
                        <div style={labelStyle}>Avg Missed</div>
                        <div style={{ ...valueStyle, color: "#f59e0b" }}>
                          {r.avg_early_exit_profit_missed ?? 0}
                        </div>
                      </div>
                    </div>
                  )}
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={labelStyle}>Active Trade Details</span>

                <button
                  style={downloadBtnStyle}
                  onClick={() =>
                    downloadExcel(
                      getActiveTradeRows(),
                      `${activeCompareSymbol}_active_trade_details_${todayFileDate}.xlsx`,
                      "Active Trades"
                    )
                  }
                >
                  <Download size={14} /> Excel
                </button>
              </div>

              {/* 2️⃣ ACTIVE TRADE DETAILS */}
              <div style={{ overflowX: "auto", marginBottom: 12 }}>
                <table style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                  color: "#e5e7eb"
                }}>
                  <thead>
                    <tr style={{ color: "#9ca3af", borderBottom: "1px solid #374151" }}>
                      <th style={{ padding: 8 }}>Algo</th>
                      <th style={{ padding: 8 }}>Status</th>
                      <th style={{ padding: 8 }}>Selection</th>
                      <th style={{ padding: 8 }}>Delta</th>
                      <th style={{ padding: 8 }}>IV</th>
                      <th style={{ padding: 8 }}>Flip Time</th>
                      <th style={{ padding: 8 }}>Flip Candle</th>
                      <th style={{ padding: 8 }}>Entry Time</th>
                      <th style={{ padding: 8 }}>Symbol</th>
                      <th style={{ padding: 8 }}>Entry</th>
                      <th style={{ padding: 8 }}>SL</th>
                      <th style={{ padding: 8 }}>Max Profit</th>
                      <th style={{ padding: 8 }}>Max Loss</th>
                    </tr>
                  </thead>

                  <tbody>
                    {getCompareRows().map((r) => (
                      <tr key={r.name} style={{ borderBottom: "1px solid #1f2937", textAlign: "center" }}>
                        <td style={{ padding: 8, fontWeight: 700 }}>{r.name}</td>
                        <td style={{ padding: 8 }}>
                          {r.active ? "🟢 Active" : r.running ? "🟡 Running" : "⚪ Waiting"}
                        </td>
                        <td style={{ padding: 8 }}>{r.selection || "-"}</td>
                        <td style={{ padding: 8 }}>{r.delta ?? "-"}</td>
                        <td style={{ padding: 8 }}>{r.iv ?? "-"}</td>
                        <td style={{ padding: 8 }}>{fmtTime(r.flip_identified_time)}</td>
                        <td style={{ padding: 8 }}>{fmtTime(r.flip_candle_time)}</td>
                        <td style={{ padding: 8 }}>{fmtTime(r.entry_time)}</td>
                        <td style={{ padding: 8 }}>{r.symbol || "-"}</td>
                        <td style={{ padding: 8 }}>{r.entry ?? "-"}</td>
                        <td style={{ padding: 8 }}>{r.sl ?? "-"}</td>
                        <td style={{ padding: 8, color: "#16c784" }}>{r.max_profit ?? 0}</td>
                        <td style={{ padding: 8, color: "#ea3943" }}>{r.max_loss ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={labelStyle}>Signal Audit</span>

                <button
                  style={downloadBtnStyle}
                  onClick={() =>
                    downloadExcel(
                      getSignalAuditRows(),
                      `${activeCompareSymbol}_signal_audit_${todayFileDate}.xlsx`,
                      "Signal Audit"
                    )
                  }
                >
                  <Download size={14} /> Excel
                </button>
              </div>

              {/* 3️⃣ SIGNAL AUDIT TABLE */}
              <div style={{ overflowX: "auto", marginBottom: 12 }}>
                <table style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                  color: "#e5e7eb"
                }}>
                  <thead>
                    <tr style={{ color: "#9ca3af", borderBottom: "1px solid #374151" }}>
                      <th style={{ padding: 8 }}>Algo</th>
                      <th style={{ padding: 8 }}>Flip Time</th>
                      <th style={{ padding: 8 }}>Flip Candle</th>
                      <th style={{ padding: 8 }}>Direction</th>
                      <th style={{ padding: 8 }}>Decision</th>
                      <th style={{ padding: 8 }}>Reason</th>
                      <th style={{ padding: 8 }}>Confirmation</th>
                      <th style={{ padding: 8 }}>ADX</th>
                      <th style={{ padding: 8 }}>ATR</th>
                      <th style={{ padding: 8 }}>ATR MA</th>
                      <th style={{ padding: 8 }}>Delta</th>
                      <th style={{ padding: 8 }}>IV</th>
                      <th style={{ padding: 8 }}>Selection</th>
                    </tr>
                  </thead>

                  <tbody>
                    {getSignalAuditRows().length === 0 ? (
                      <tr>
                        <td colSpan="13" style={{ padding: 12, textAlign: "center", color: "#9ca3af" }}>
                          No signal audit data yet.
                        </td>
                      </tr>
                    ) : (
                      getSignalAuditRows().map((r, idx) => (
                        <tr key={`${r.algo}-${idx}`} style={{ borderBottom: "1px solid #1f2937", textAlign: "center" }}>
                          <td style={{ padding: 8, fontWeight: 700 }}>{r.algo}</td>
                          <td style={{ padding: 8 }}>{fmtTime(r.flip_identified_time)}</td>
                          <td style={{ padding: 8 }}>{fmtTime(r.flip_candle_time)}</td>
                          <td style={{ padding: 8 }}>{r.direction || "-"}</td>
                          <td style={{
                            padding: 8,
                            color: r.decision === "ENTERED" ? "#16c784" : "#f59e0b",
                            fontWeight: 700
                          }}>
                            {r.decision || "-"}
                          </td>
                          <td style={{ padding: 8 }}>{r.reason || "-"}</td>
                          <td style={{ padding: 8 }}>{String(r.confirmation ?? "-")}</td>
                          <td style={{ padding: 8 }}>
                            {r.adx ?? "-"} {r.adx_pass === false ? "❌" : r.adx_pass === true ? "✅" : ""}
                          </td>
                          <td style={{ padding: 8 }}>
                            {r.atr ?? "-"} {r.atr_pass === false ? "❌" : r.atr_pass === true ? "✅" : ""}
                          </td>
                          <td style={{ padding: 8 }}>{r.atr_ma ?? "-"}</td>
                          <td style={{ padding: 8 }}>{r.delta ?? "-"}</td>
                          <td style={{ padding: 8 }}>{r.iv ?? "-"}</td>
                          <td style={{ padding: 8 }}>{r.selection || "-"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={labelStyle}>Flip History - Every Supertrend Flip Identified</span>

                <button
                  style={downloadBtnStyle}
                  onClick={() =>
                    downloadExcel(
                      getFlipHistoryRows(),
                      `${activeCompareSymbol}_flip_history_${todayFileDate}.xlsx`,
                      "Flip History"
                    )
                  }
                >
                  <Download size={14} /> Excel
                </button>
              </div>

              {/* 4️⃣ FLIP HISTORY TABLE */}
              <div style={{ overflowX: "auto", marginBottom: 12 }}>
                <table style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                  color: "#e5e7eb"
                }}>
                  <thead>
                    <tr style={{ color: "#9ca3af", borderBottom: "1px solid #374151" }}>
                      <th style={{ padding: 8 }}>Algo</th>
                      <th style={{ padding: 8 }}>Flip Time</th>
                      <th style={{ padding: 8 }}>Candle Time</th>
                      <th style={{ padding: 8 }}>Direction</th>
                      <th style={{ padding: 8 }}>Decision</th>
                      <th style={{ padding: 8 }}>Reason</th>
                      <th style={{ padding: 8 }}>Confirmation</th>
                      <th style={{ padding: 8 }}>ADX</th>
                      <th style={{ padding: 8 }}>ATR</th>
                      <th style={{ padding: 8 }}>Delta</th>
                      <th style={{ padding: 8 }}>Selection</th>
                    </tr>
                  </thead>

                  <tbody>
                    {getFlipHistoryRows().length === 0 ? (
                      <tr>
                        <td colSpan="11" style={{ padding: 12, textAlign: "center", color: "#9ca3af" }}>
                          No flip history yet. Backend should send flip_history for each algo.
                        </td>
                      </tr>
                    ) : (
                      getFlipHistoryRows().map((r, idx) => (
                        <tr key={`${r.Algo}-flip-${idx}`} style={{ borderBottom: "1px solid #1f2937", textAlign: "center" }}>
                          <td style={{ padding: 8, fontWeight: 700 }}>{r.Algo}</td>
                          <td style={{ padding: 8 }}>{r["Flip Time"]}</td>
                          <td style={{ padding: 8 }}>{r["Candle Time"]}</td>
                          <td style={{
                            padding: 8,
                            color: r.Direction === "UP" ? "#16c784" : r.Direction === "DOWN" ? "#ea3943" : "#e5e7eb",
                            fontWeight: 700
                          }}>
                            {r.Direction}
                          </td>
                          <td style={{
                            padding: 8,
                            color: r.Decision === "ENTERED" || r.Decision === "ENTER_READY" ? "#16c784" : r.Decision === "SKIPPED" ? "#f59e0b" : "#e5e7eb",
                            fontWeight: 700
                          }}>
                            {r.Decision}
                          </td>
                          <td style={{ padding: 8 }}>{r.Reason}</td>
                          <td style={{ padding: 8 }}>{String(r.Confirmation)}</td>
                          <td style={{ padding: 8 }}>{r.ADX}</td>
                          <td style={{ padding: 8 }}>{r.ATR}</td>
                          <td style={{ padding: 8 }}>{r.Delta}</td>
                          <td style={{ padding: 8 }}>{r.Selection}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={labelStyle}>Full Trade History - Strike, Entry, Exit and PnL</span>

                <button
                  style={downloadBtnStyle}
                  onClick={() =>
                    downloadExcel(
                      getTradeHistoryRows(),
                      `${activeCompareSymbol}_trade_history_${todayFileDate}.xlsx`,
                      "Trade History"
                    )
                  }
                >
                  <Download size={14} /> Excel
                </button>
              </div>

              {/* 5️⃣ FULL TRADE HISTORY TABLE */}
              <div style={{ overflowX: "auto", marginBottom: 12 }}>
                <table style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                  color: "#e5e7eb"
                }}>
                  <thead>
                    <tr style={{ color: "#9ca3af", borderBottom: "1px solid #374151" }}>
                      <th style={{ padding: 8 }}>Algo</th>
                      <th style={{ padding: 8 }}>Entry Time</th>
                      <th style={{ padding: 8 }}>Exit Time</th>
                      <th style={{ padding: 8 }}>Direction</th>
                      <th style={{ padding: 8 }}>Side</th>
                      <th style={{ padding: 8 }}>Strike Symbol</th>
                      <th style={{ padding: 8 }}>Strike</th>
                      <th style={{ padding: 8 }}>Entry</th>
                      <th style={{ padding: 8 }}>Exit</th>
                      <th style={{ padding: 8 }}>PnL</th>
                      <th style={{ padding: 8 }}>Exit Reason</th>
                      <th style={{ padding: 8 }}>Exit Type</th>
                      <th style={{ padding: 8 }}>Smart Score</th>
                      <th style={{ padding: 8 }}>Smart Reason</th>
                      <th style={{ padding: 8 }}>Delta</th>
                      <th style={{ padding: 8 }}>IV</th>
                      <th style={{ padding: 8 }}>Max Profit</th>
                      <th style={{ padding: 8 }}>Max Loss</th>
                    </tr>
                  </thead>

                  <tbody>
                    {getTradeHistoryRows().length === 0 ? (
                      <tr>
                        <td colSpan="18" style={{ padding: 12, textAlign: "center", color: "#9ca3af" }}>
                          No completed trade history yet. This table fills after entries exit.
                        </td>
                      </tr>
                    ) : (
                      getTradeHistoryRows().map((r, idx) => (
                        <tr key={`${r.Algo}-trade-${idx}`} style={{ borderBottom: "1px solid #1f2937", textAlign: "center" }}>
                          <td style={{ padding: 8, fontWeight: 700 }}>{r.Algo}</td>
                          <td style={{ padding: 8 }}>{r["Entry Time"]}</td>
                          <td style={{ padding: 8 }}>{r["Exit Time"]}</td>
                          <td style={{
                            padding: 8,
                            color: r.Direction === "UP" ? "#16c784" : r.Direction === "DOWN" ? "#ea3943" : "#e5e7eb",
                            fontWeight: 700
                          }}>
                            {r.Direction}
                          </td>
                          <td style={{ padding: 8 }}>{r.Side}</td>
                          <td style={{ padding: 8, fontWeight: 700 }}>{r.Symbol}</td>
                          <td style={{ padding: 8 }}>{r.Strike}</td>
                          <td style={{ padding: 8 }}>{r.Entry}</td>
                          <td style={{ padding: 8 }}>{r.Exit}</td>
                          <td style={{
                            padding: 8,
                            fontWeight: 700,
                            color: (Number(r.PnL) || 0) >= 0 ? "#16c784" : "#ea3943"
                          }}>
                            {r.PnL}
                          </td>
                          <td style={{ padding: 8 }}>{r.Reason}</td>
                          <td style={{ padding: 8 }}>{r["Exit Type"]}</td>
                          <td style={{ padding: 8 }}>{r["Smart Score"]}</td>
                          <td style={{ padding: 8 }}>{r["Smart Reason"]}</td>
                          <td style={{ padding: 8 }}>{r.Delta}</td>
                          <td style={{ padding: 8 }}>{r.IV}</td>
                          <td style={{ padding: 8, color: "#16c784" }}>{r["Max Profit"]}</td>
                          <td style={{ padding: 8, color: "#ea3943" }}>{r["Max Loss"]}</td>                          
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={labelStyle}>Final Algo Comparison</span>

                <button
                  style={downloadBtnStyle}
                  onClick={() =>
                    downloadExcel(
                      getSummaryRows(),
                      `${activeCompareSymbol}_final_algo_comparison_${todayFileDate}.xlsx`,
                      "Comparison"
                    )
                  }
                >
                  <Download size={14} /> Excel
                </button>
              </div>

              {/* 🔥 ALGO COMPARE TABLE */}
              <div style={{ overflowX: "auto" }}>
                <table style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                  color: "#e5e7eb"
                }}>
                  <thead>
                    <tr style={{ color: "#9ca3af", borderBottom: "1px solid #374151" }}>
                      <th style={{ padding: "8px" }}>Strategy</th>
                      <th style={{ padding: "8px" }}>Status</th>
                      <th style={{ padding: "8px" }}>Active Symbol</th>
                      <th style={{ padding: "8px" }}>Entry</th>
                      <th style={{ padding: "8px" }}>SL</th>
                      <th style={{ padding: "8px" }}>Trades</th>
                      <th style={{ padding: "8px" }}>Wins</th>
                      <th style={{ padding: "8px" }}>Losses</th>
                      <th style={{ padding: "8px" }}>Win %</th>
                      <th style={{ padding: "8px" }}>Net Points</th>
                      <th style={{ padding: "8px" }}>Profit Factor</th>
                      <th style={{ padding: "8px" }}>Avg Win</th>
                      <th style={{ padding: "8px" }}>Avg Loss</th>
                      <th style={{ padding: "8px" }}>Max DD</th>
                      <th style={{ padding: "8px" }}>Smart Exits</th>
                      <th style={{ padding: "8px" }}>Good Smart</th>
                      <th style={{ padding: "8px" }}>False Smart</th>
                      <th style={{ padding: "8px" }}>Smart Accuracy</th>
                      <th style={{ padding: "8px" }}>Avg Saved</th>
                      <th style={{ padding: "8px" }}>Avg Missed</th>
                      <th style={{ padding: "8px" }}>Selection</th>
                      <th style={{ padding: "8px" }}>Delta</th>
                      <th style={{ padding: "8px" }}>IV</th>
                      <th style={{ padding: "8px" }}>Rank</th>
                    </tr>
                  </thead>

                  <tbody>
                    {getCompareRows().length === 0 ? (
                      <tr>
                        <td colSpan="24" style={{ padding: "12px", textAlign: "center", color: "#9ca3af" }}>
                          No comparison data yet.
                        </td>
                      </tr>
                    ) : (
                      getCompareRows().map((r) => (
                        <tr key={r.name} style={{ borderBottom: "1px solid #1f2937", textAlign: "center" }}>
                          <td style={{ padding: "8px", fontWeight: "700" }}>{r.name}</td>
                          <td style={{ padding: "8px" }}>
                            {r.active ? "🟢 Active" : r.running ? "🟡 Running" : r.stopped ? "🔴 Stopped" : "⚪ Waiting"}
                          </td>
                          <td style={{ padding: "8px" }}>{r.symbol || "-"}</td>
                          <td style={{ padding: "8px" }}>{r.entry ?? "-"}</td>
                          <td style={{ padding: "8px" }}>{r.sl ?? "-"}</td>
                          <td style={{ padding: "8px" }}>{r.completed_trades ?? 0}</td>
                          <td style={{ padding: "8px" }}>{r.wins ?? 0}</td>
                          <td style={{ padding: "8px" }}>{r.losses ?? 0}</td>
                          <td style={{ padding: "8px" }}>{r.win_rate ?? 0}%</td>
                          <td style={{
                            padding: "8px",
                            fontWeight: "700",
                            color: (r.net_points ?? 0) >= 0 ? "#16c784" : "#ea3943"
                          }}>
                            {r.net_points ?? 0}
                          </td>
                          <td style={{ padding: "8px" }}>{r.profit_factor ?? 0}</td>
                          <td style={{ padding: "8px" }}>{r.avg_win ?? 0}</td>
                          <td style={{ padding: "8px" }}>{r.avg_loss ?? 0}</td>
                          <td style={{ padding: "8px" }}>{r.max_drawdown ?? 0}</td>
                          <td style={{ padding: "8px" }}>{r.early_exit_count ?? 0}</td>
                          <td style={{ padding: "8px", color: "#16c784" }}>{r.good_early_exit_count ?? 0}</td>
                          <td style={{ padding: "8px", color: "#ea3943" }}>{r.false_early_exit_count ?? 0}</td>
                          <td style={{ padding: "8px" }}>{r.early_exit_accuracy ?? 0}%</td>
                          <td style={{ padding: "8px", color: "#16c784" }}>{r.avg_early_exit_points_saved ?? 0}</td>
                          <td style={{ padding: "8px", color: "#f59e0b" }}>{r.avg_early_exit_profit_missed ?? 0}</td>
                          <td style={{ padding: "8px" }}>{r.selection || "-"}</td>
                          <td style={{ padding: "8px" }}>{r.delta ?? "-"}</td>
                          <td style={{ padding: "8px" }}>{r.iv ?? "-"}</td>
                          <td style={{ padding: "8px", fontWeight: "700" }}>
                            {r.rank ? `#${r.rank}` : "-"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* 🔥 RIGHT - POSITIONS & ORDERS */}
          {/* 🔥 RIGHT SIDE SPLIT */}
          <div className="algo-execution-grid" style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 12,
                marginTop: 2
            }}
          >
              {/* 🔹 POSITIONS (TOP PRIORITY) */}
              <div className="panel" style={{ flex: 1 }}>
                <div className="panel-title">Admin Positions</div>
                <Positions positions={algoAdminPositions} />
              </div>

              <div className="panel" style={{ flex: 1 }}>
                <div className="panel-title">Client Positions</div>
                <Positions
                  positions={algoClientPositions}
                  allowReconciliation
                />
              </div>
              {/* 🔹 ORDERS (BOTTOM PRIORITY) */}
              <div className="panel" style={{ flex: 1 }}>
                <div className="panel-title">Algo Orders</div>
                <Orders orders={algoOrders} />
              </div>
            </div>            
          </div>
      </div>

      <div style={{ display: activeTab === "TRADE_MANAGEMENT" ? "block" : "none" }}>
        <div className="trade-management-workspace">
          <div className="panel">
            <div className="panel-title">Copy Trade Overview</div>
            <div className="panel-body">
              <CopyTradeGist />
            </div>
          </div>

          <div className="trade-management-grid">
            <div className="panel">
              <div className="panel-title">Trade Controls & Admin Position</div>
              <div className="panel-body">
                <Positions
                  positions={copyAdminPositions}
                  showActions
                  enableTrailStop
                  trailStatus={dashboard.copy_trade_trail}
                  actionsDisabled={wsStatus !== "CONNECTED"}
                />
              </div>
            </div>

            <div className="panel">
              <div className="panel-title">Client Positions</div>
              <div className="panel-body">
                <Positions
                  positions={copyClientPositions}
                  allowReconciliation
                />
              </div>
            </div>

            <div className="panel">
              <div className="panel-title">Trade Orders</div>
              <div className="panel-body">
                <Orders orders={copyOrders} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: activeTab === "CLIENTS" ? "block" : "none" }}>
        <div className="clients-workspace">
          <div className="panel clients-primary-panel">
            <div className="panel-title">Client Accounts</div>
            <div className="panel-body">
              <ClientsTable
                active={activeTab === "CLIENTS"}
                clients={dashboard.clients}
                loginStatus={loginStatus}
                onLogin={loginClient}
                onLogout={logoutClient}
                loadingClients={loadingClients}
                onAlgoProductsChange={updateClientAlgoProduct}
                onIndexLotsChange={updateClientIndexLots}
                onAutoStockChange={updateClientAutoStock}
                onAutoStockCapitalChange={updateClientAutoStockCapital}
                onCapitalChange={updateClientCapital}
                onCapitalReset={resetClientCapital}
              />
            </div>
          </div>

        </div>
      </div>

      <div style={{ display: activeTab === "SYSTEM" ? "block" : "none" }}>
        <div className="workspace-grid system-workspace">
          <div className="panel">
            <div className="panel-title">System Readiness</div>
            <div className="panel-body compact-panel-body">
              <HealthMetric label="Overall System" value={systemReady ? "Ready" : systemStatus.init_in_progress ? "Starting" : "Not Ready"} tone={systemReady ? "ok" : "bad"} />
              <HealthMetric label="Admin" value={loginStatus.admin ? "Online" : "Offline"} tone={loginStatus.admin ? "ok" : "bad"} />
              <HealthMetric label="Ready Clients" value={`${readyClients.length}/${activeClients.length}`} tone={readyClients.length ? "ok" : "bad"} />
              <HealthMetric label="Online Clients" value={`${onlineClients.length}/${activeClients.length}`} tone={onlineClients.length === activeClients.length ? "ok" : "warn"} />
              {systemStatus.initialization_error && <HealthMetric label="Initialization" value={systemStatus.initialization_error} tone="bad" />}
            </div>
          </div>

          <div className="panel">
            <div className="panel-title">Market Data</div>
            <div className="panel-body compact-panel-body">
              <HealthMetric label="Feed Connection" value={marketConnected ? "Connected" : "Disconnected"} tone={marketConnected ? "ok" : "bad"} />
              <HealthMetric label="Last Tick" value={fmtTime(systemStatus.market_data?.last_tick_at)} tone={systemStatus.market_data?.last_tick_at ? "ok" : "warn"} />
              <HealthMetric label="Subscribed Instruments" value={systemStatus.market_data?.subscribed_tokens ?? 0} tone="muted" />
              <HealthMetric label="Instruments Ready" value={systemStatus.market_data?.ready_tokens ?? 0} tone="muted" />
            </div>
          </div>

          <div className="panel">
            <div className="panel-title">Live Execution</div>
            <div className="panel-body compact-panel-body">
              <HealthMetric label="Queue Depth" value={systemStatus.queue_depth ?? pendingOrders.length} tone={Number(systemStatus.queue_depth ?? pendingOrders.length) ? "warn" : "ok"} />
              <HealthMetric label="Queued / Processing" value={pendingOrders.length} tone={pendingOrders.length ? "warn" : "ok"} />
              <HealthMetric label="Oldest Pending Order" value={oldestPendingAt ? fmtTime(oldestPendingAt) : "None"} tone={oldestPendingAt ? "warn" : "ok"} />
              <HealthMetric label="Completed in Visible History" value={visibleCompletedOrders.length} tone="muted" />
              <HealthMetric label="Failed in Visible History" value={visibleFailedOrders.length} tone={visibleFailedOrders.length ? "bad" : "ok"} />
            </div>
          </div>

          <div className="panel">
            <div className="panel-title">Broker Execution</div>
            <div className="panel-body compact-panel-body">
              <HealthMetric label="Enabled Broker Batches" value={enabledBatchCount} tone={enabledBatchCount ? "ok" : "warn"} />
              {Object.entries(brokerBatches).map(([name, batch]) => <HealthMetric key={name} label={name} value={batch?.enabled === false ? "Disabled" : "Enabled"} tone={batch?.enabled === false ? "warn" : "ok"} />)}
            </div>
          </div>

          <div className="panel system-wide-panel">
            <div className="panel-title">Actionable Client Warnings <small>{runtimeWarnings.length ? `${runtimeWarnings.length} client(s) need attention` : "No current issues"}</small></div>
            <div className="panel-body compact-panel-body runtime-warning-list">
              {runtimeWarnings.length ? runtimeWarnings.map(item => <RuntimeWarning key={item.client.id} item={item} />) : <HealthMetric label="Clients, API and Position Sync" value="Healthy" tone="ok" />}
            </div>
          </div>

          <details className="panel system-wide-panel system-technical-details">
            <summary className="panel-title">Technical Routing Details</summary>
            <div className="panel-body compact-panel-body">
              <HealthMetric label="Default IP Group" value={defaultIpGroup} tone="muted" />
              <HealthMetric label="Configured Clients" value={systemStatus.clients ?? dashboard.clients.length} tone="muted" />
              <HealthMetric label="Duplicate-order Guard" value={systemStatus.duplicate_guard ? "Active" : "Unavailable"} tone={systemStatus.duplicate_guard ? "ok" : "warn"} />
            </div>
          </details>
        </div>
      </div>
      </div>
      </div>
    </div>
  );
}
